import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Database,
  LayoutDashboard,
  Loader2,
  LogOut,
  Package,
  ScrollText,
  ShieldAlert,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { getCurrentUser, PlatformApiError, type PlatformUser, signOut } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform')({
  component: PlatformLayout,
})

/**
 * Platform admin shell. Runs a role check on mount and renders one
 * of three states:
 *
 *   - loading: spinner while /me resolves
 *   - forbidden: 403 page for customer_owner / unauthenticated
 *   - authed: the admin layout (nav tabs + Outlet)
 *
 * The role gate is frontend-side courtesy; backend admin endpoints
 * enforce the same check server-side via requirePlatformRole. A
 * determined attacker can't read data by skipping this component —
 * they'd still 403 at the API level.
 */
const ADMIN_ROLES = ['platform_admin', 'platform_support', 'platform_readonly']

function PlatformLayout() {
  const navigate = useNavigate()
  const location = useLocation()

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'authed'; user: PlatformUser }
    | { status: 'forbidden'; reason: 'unauthenticated' | 'not_admin'; role: string | null }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    const ctrl = new AbortController()
    getCurrentUser(ctrl.signal)
      .then((user) => {
        if (user.role && ADMIN_ROLES.includes(user.role)) {
          setState({ status: 'authed', user })
        } else {
          setState({ status: 'forbidden', reason: 'not_admin', role: user.role })
        }
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (err instanceof PlatformApiError && err.status === 401) {
          setState({ status: 'forbidden', reason: 'unauthenticated', role: null })
          return
        }
        const message = err instanceof PlatformApiError ? err.message : 'Failed to load session'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  async function handleSignOut() {
    try {
      await signOut()
    } catch {
      // ignore — we navigate regardless
    }
    navigateRef.current({ to: '/sign-in' })
  }

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        {state.status === 'loading' ? (
          <LoadingShell />
        ) : state.status === 'forbidden' ? (
          <ForbiddenShell reason={state.reason} role={state.role} />
        ) : state.status === 'error' ? (
          <ErrorShell message={state.message} />
        ) : (
          <AuthedShell
            user={state.user}
            currentPath={location.pathname}
            onSignOut={handleSignOut}
          />
        )}
      </main>
      <Footer />
    </>
  )
}

function LoadingShell() {
  return (
    <section className="flex flex-1 items-center justify-center pt-24 pb-24">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading platform admin…</span>
      </div>
    </section>
  )
}

function ForbiddenShell({
  reason,
  role,
}: {
  reason: 'unauthenticated' | 'not_admin'
  role: string | null
}) {
  return (
    <section className="relative flex flex-1 items-center justify-center pt-24 pb-24">
      <div
        className="pointer-events-none absolute inset-0 -z-10 marketing-grid-bg opacity-20"
        aria-hidden="true"
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-md px-4 text-center"
      >
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive ring-1 ring-destructive/15">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <h1 className="mt-4 text-balance text-2xl font-bold tracking-tight sm:text-3xl">
          {reason === 'unauthenticated' ? 'Sign in required' : 'Platform access required'}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {reason === 'unauthenticated'
            ? 'You need to sign in with a platform account to access this area.'
            : `Your account role (${role ?? 'none'}) does not have permission to view the platform admin. Contact a platform administrator if you think this is a mistake.`}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {reason === 'unauthenticated' ? (
            <Link
              to="/sign-in"
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
              )}
            >
              Sign in
            </Link>
          ) : (
            <Link
              to="/account"
              className={cn(
                'inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-5 py-2 text-sm font-medium backdrop-blur',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
              )}
            >
              Back to account
            </Link>
          )}
        </div>
      </motion.div>
    </section>
  )
}

function ErrorShell({ message }: { message: string }) {
  return (
    <section className="flex flex-1 items-center justify-center pt-24 pb-24">
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
        {message}
      </div>
    </section>
  )
}

function AuthedShell({
  user,
  currentPath,
  onSignOut,
}: {
  user: PlatformUser
  currentPath: string
  onSignOut: () => void
}) {
  return (
    <section className="flex-1 pt-24 pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Admin header strip */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Platform admin
            </div>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
              <span className="marketing-gradient-text">Cloud ERP Control</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="text-right">
              <div className="font-medium">{user.email}</div>
              <div className="text-xs text-muted-foreground">{user.role ?? '—'}</div>
            </div>
            <button
              type="button"
              onClick={onSignOut}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
              )}
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </motion.header>

        {/* Sub-nav */}
        <AdminSubNav currentPath={currentPath} />

        {/* Route content */}
        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </section>
  )
}

function AdminSubNav({ currentPath }: { currentPath: string }) {
  const items = [
    { to: '/platform', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/platform/tenants', label: 'Tenants', Icon: Database },
    { to: '/platform/users', label: 'Users', Icon: Users },
    { to: '/platform/modules', label: 'Modules', Icon: Package },
    { to: '/platform/audit', label: 'Audit log', Icon: ScrollText },
  ] as const

  return (
    <nav className="border-b border-border">
      <div className="flex gap-1">
        {items.map(({ to, label, Icon }) => {
          const active = to === '/platform' ? currentPath === to : currentPath.startsWith(to)
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                'inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                active
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
